import React from 'react'
import type { RubroRow } from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  normalizeDocumentationFileIds,
  normalizeDocumentationSlots,
  rubroHasValidSelectedSkill
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  resolveBudgetSheetReadonlyReason,
  rubroBudgetSlotHasFileOrPages
} from '../../../../../utils/conversationalMode/budgetSheetStateUtils.ts'
import { showQuotationOrphanAfterMetricRemoval } from '../../../../../utils/conversationalMode/rubroMetricBudgetCoherenceUtils.ts'
import {
  canEditBudgetSheetState,
  rubroBudgetLegajoInPdf,
  rubroMetricReadyFromRow,
  type DocumentationReviewContext
} from '../../../../../utils/conversationalMode/projectAutofillWorkflow.ts'
import RubroDeliverableSlotActions from './RubroDeliverableSlotActions.tsx'
import { buildSlotOriginActionMeta } from './rubroDeliverableOriginUtils.ts'
import type { RubroSlotOriginHandlers } from './RubroSlotOriginHandlers.ts'
import type { RubroDeliverableSourceLineProps } from './rubroDeliverableSourceLineProps.ts'
import styles from './BudgetSheetCompactEntry.module.scss'
import ProjectAutofillInfoNote from './ProjectAutofillInfoNote.tsx'
import type { MetricBudgetSyncPhase } from './projectAutofillCard/hooks/useRubroMetricBudgetSilentSync.ts'

type SourceLineProps = RubroDeliverableSourceLineProps

type Props = {
  tempId: string
  rubro: RubroRow
  draftId: string | undefined
  draftLocked: boolean
  isSaving: boolean
  documentationReview: DocumentationReviewContext
  updateRubro: (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => void
  onOpenFullscreen: (tempId: string) => void
  embedded?: boolean
  sourceLineProps?: SourceLineProps
  originHandlers: RubroSlotOriginHandlers
  originPickerOpen: boolean
  onOriginPickerOpenChange: (open: boolean) => void
  removedSnapshot: Partial<Omit<RubroRow, 'tempId'>> | null
  onRemovedSnapshotChange: (snapshot: Partial<Omit<RubroRow, 'tempId'>> | null) => void
  metricRestoreSnapshot?: Partial<Omit<RubroRow, 'tempId'>> | null
  onRestoreMetricPackage?: () => void
  budgetSyncPhase?: MetricBudgetSyncPhase
}

const BudgetSheetCompactEntry: React.FC<Props> = ({
  tempId,
  rubro,
  draftId,
  draftLocked,
  isSaving,
  documentationReview,
  updateRubro,
  onOpenFullscreen,
  embedded = false,
  sourceLineProps,
  originHandlers,
  originPickerOpen,
  onOriginPickerOpenChange,
  removedSnapshot,
  onRemovedSnapshotChange,
  metricRestoreSnapshot,
  onRestoreMetricPackage,
  budgetSyncPhase = 'idle'
}) => {
  if (!draftId || draftLocked) return null

  const slotsB = normalizeDocumentationSlots(rubro.documentationSlots ?? {})
  const idsB = normalizeDocumentationFileIds(rubro.documentationFileIds ?? {})
  const budgetBusy = rubroBudgetSlotHasFileOrPages(slotsB, idsB)
  const bs = rubro.budgetSheetState
  const metricReady = rubroMetricReadyFromRow(rubro)
  const legajoPdf = rubroBudgetLegajoInPdf(rubro)
  const workflowAllowsQuotation = documentationReview.isQuotationReview
  const canEditQuotation = canEditBudgetSheetState(
    rubro.quotationEligibility,
    documentationReview.reviewStage
  )
  const ro = resolveBudgetSheetReadonlyReason(
    {
      tempId,
      detectedLabel: rubro.detectedLabel,
      selectedSkillId: rubro.selectedSkillId
    },
    bs,
    budgetBusy,
    {
      metricReady,
      workflowAllowsQuotation,
      legajoPdfOnly: legajoPdf && workflowAllowsQuotation && !bs
    }
  )
  const canSkill = rubroHasValidSelectedSkill(rubro)
  const saving = isSaving || !draftId
  const wrapClass = embedded ? styles.blockEmbedded : styles.block
  const showRemove = legajoPdf || budgetBusy || !!bs
  const originAction =
    legajoPdf && sourceLineProps
      ? buildSlotOriginActionMeta({ slot: 'budget', ...sourceLineProps })
      : null
  const sheetMeta = legajoPdf && !bs
    ? originAction?.meta ?? null
    : bs
      ? bs.rows.length === 0
        ? 'Sin filas todavía'
        : `${bs.rows.length} fila${bs.rows.length === 1 ? '' : 's'}`
      : null
  const sheetPdfHref = legajoPdf && !bs ? originAction?.pdfHref ?? null : null

  const handleOpenFullscreen = () => {
    if (!canSkill || ro !== 'none' || saving || !canEditQuotation || !bs) return
    onOpenFullscreen(tempId)
  }

  const slotActions = showRemove || originPickerOpen ? (
    <RubroDeliverableSlotActions
      slot="budget"
      showRemove={showRemove}
      showPrimary={canSkill && !!bs}
      primaryDisabled={saving || ro !== 'none' || !canEditQuotation}
      onPrimary={handleOpenFullscreen}
      meta={sheetMeta}
      pdfHref={sheetPdfHref}
      originPickerOpen={originPickerOpen}
      onOriginPickerOpenChange={onOriginPickerOpenChange}
      removedSnapshot={removedSnapshot}
      onRemovedSnapshotChange={onRemovedSnapshotChange}
      {...originHandlers}
    />
  ) : null

  if (originPickerOpen) {
    return <div className={wrapClass}>{slotActions}</div>
  }

  if (!workflowAllowsQuotation) {
    const hint = documentationReview.isProcessing
      ? 'Las planillas se generan mientras se analiza el documento.'
      : documentationReview.isMetricOnly
        ? documentationReview.sheetGenerationOutcome === 'failed'
          ? 'No generamos la planilla de cotización automáticamente. Revisá el cómputo o re-analizá el documento.'
          : 'Re-analizá el documento para obtener las planillas de cotización sugeridas.'
        : 'La cotización estará disponible cuando termine el análisis del documento.'
    return (
      <div className={wrapClass}>
        {!embedded ? <h4 className={styles.title}>Planilla de cotización</h4> : null}
        <ProjectAutofillInfoNote className={styles.hint}>{hint}</ProjectAutofillInfoNote>
      </div>
    )
  }

  if (rubro.quotationEligibility === 'skipped_no_metric') {
    return (
      <div className={wrapClass}>
        <h4 className={styles.title}>Planilla de cotización</h4>
        <ProjectAutofillInfoNote className={styles.hint}>
          Este rubro no tiene cómputo métrico usable. Podés publicar la obra sin presupuesto en este rubro.
        </ProjectAutofillInfoNote>
      </div>
    )
  }

  const quotationOrphan = showQuotationOrphanAfterMetricRemoval(rubro, metricRestoreSnapshot)

  if (quotationOrphan) {
    return (
      <div className={wrapClass}>
        <ProjectAutofillInfoNote tone="caution" className={styles.hint}>
          Sin cómputo métrico en este rubro no se adjuntará la planilla de cotización que habíamos generado. Si
          querés conservarla junto con el cómputo original, restaurala desde la pestaña Cómputo métrico o con el
          botón de abajo.
        </ProjectAutofillInfoNote>
        {onRestoreMetricPackage ? (
          <div className={styles.sheetActionRow}>
            <span className={styles.sheetActionRowSpacer} aria-hidden />
            <div className={styles.sheetActionGroup}>
              <button
                type="button"
                className={styles.restoreInline}
                disabled={saving}
                onClick={onRestoreMetricPackage}
              >
                Restaurar cómputo y planilla
              </button>
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  if (rubro.quotationEligibility === 'quotation_generation_failed') {
    return (
      <div className={wrapClass}>
        <p className={styles.sheetOfferText}>
          <strong>No generamos</strong> planilla de cotización para este rubro. Podés completarla manualmente si la
          necesitás.
        </p>
      </div>
    )
  }

  if (legajoPdf && !bs) {
    return (
      <div className={wrapClass}>
        <p className={styles.sheetOfferText}>
          <strong>Adjuntaremos</strong> el presupuesto desde las páginas del PDF detectadas para este rubro.
        </p>
        {slotActions}
      </div>
    )
  }

  if (!canEditQuotation && !bs) {
    return (
      <div className={wrapClass}>
        <h4 className={styles.title}>Planilla de cotización</h4>
        <ProjectAutofillInfoNote className={styles.hint}>
          La planilla de cotización no está disponible para este rubro.
        </ProjectAutofillInfoNote>
      </div>
    )
  }

  const budgetSyncBusy = budgetSyncPhase === 'pending' || budgetSyncPhase === 'syncing'

  return (
    <div className={wrapClass}>
      {bs && metricReady && budgetSyncBusy ? (
        <ProjectAutofillInfoNote variant="rubroInfo" className={styles.hint}>
          Actualizando planilla según el cómputo…
        </ProjectAutofillInfoNote>
      ) : null}
      <p className={styles.sheetOfferText}>
        <strong>Generamos</strong> la planilla de cotización para este rubro. Revisá precios e ítems antes de publicar.
      </p>
      {!canSkill ? (
        <ProjectAutofillInfoNote className={styles.hint}>Elegí el rubro de la lista para editar.</ProjectAutofillInfoNote>
      ) : null}
      {slotActions}
    </div>
  )
}

export default BudgetSheetCompactEntry
